import { parseISO } from "date-fns";

import { formatDateTime } from "@performyard/py-components";

/**
 * Formats a workflow date for display. `dateSize: "large"` spells the month out in
 * full ("February 5, 2026") for the detail page heading.
 *
 * These dates are stored at midnight UTC and are calendar dates, not instants, so
 * the string is truncated to `YYYY-MM-DD` and parsed as a local date instead of
 * being converted to the viewer's timezone — which can shift the day. Do not
 * "simplify" this into a timezone-aware conversion.
 */
export const formatUtcDate = (value: unknown, dateSize: "medium" | "large" = "medium"): string => {
	if (!value) {
		return "";
	}
	return formatDateTime(parseISO(String(value).slice(0, 10)), { hasTime: false, dateSize }) ?? "";
};

/**
 * Formats a `Date` already in local time for a filter chip.
 *
 * Unlike `formatUtcDate`, the input here comes from a date picker rather than the
 * API, so there is no UTC-midnight string to truncate.
 */
export const formatChipDate = (date: Date): string =>
	formatDateTime(date, { hasTime: false, dateSize: "medium" }) ?? "";

/** Chip text for an open-ended or closed date range. Shared by the workflows filter chips. */
export const describeDateRange = (start?: Date, end?: Date): string => {
	if (start && end) {
		return `${formatChipDate(start)} – ${formatChipDate(end)}`;
	}
	if (start) {
		return `on or after ${formatChipDate(start)}`;
	}
	return `on or before ${formatChipDate(end as Date)}`;
};
